import type { FunctionRow, Team, TeamMemberInput } from '@pm/schemas';
import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LabelCaps,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  PageHeader,
  Select,
  SkeletonRows,
} from '@pm/ui';
import { MoreHorizontal, Pencil, Plus, Trash2, Users, X } from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { UsersMultiPicker } from '@/components/pickers';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { keys, useFunctions, useInvalidating, useTeams, useUsers } from '@/lib/queries';
import { useToast } from '@/lib/toast';

/** SPEC §5 — times com múltiplos usuários, funções e gestores. */
export function TeamsPage() {
  const toast = useToast();
  const { can } = useAuth();
  const { data: teams = [], isLoading, isError, error, refetch } = useTeams();
  const [editing, setEditing] = useState<Team | 'new' | null>(null);
  const [remove, setRemove] = useState<Team | null>(null);
  const del = useInvalidating((id: string) => api.delete(`/teams/${id}`), [keys.teams]);

  return (
    <div className="flex max-w-[1100px] flex-col gap-gap-lg">
      <PageHeader
        title="Times"
        subtitle="Um usuário pode estar em vários times, com uma ou mais funções em cada."
        actions={
          can('teams.create') ? (
            <Button variant="primary" onClick={() => setEditing('new')}>
              <Plus /> Novo time
            </Button>
          ) : null
        }
      />
      <div className="grid gap-gap-lg lg:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-gap">
          {isLoading ? (
            <div className="rounded-md border border-border bg-surface-1">
              <SkeletonRows />
            </div>
          ) : isError ? (
            <ErrorState message={error.message} onRetry={() => void refetch()} />
          ) : teams.length === 0 ? (
            <div className="rounded-md border border-border bg-surface-1">
              <EmptyState
                icon={Users}
                title="Nenhum time"
                description="Times organizam pessoas por função e dão visibilidade a gestores."
              />
            </div>
          ) : (
            teams.map((t) => (
              <section key={t.id} className="rounded-md border border-border bg-surface-1 p-4">
                <header className="mb-3 flex items-center gap-2">
                  <h2 className="text-lg font-medium">{t.name}</h2>
                  {t.status === 'INACTIVE' ? <Badge tone="neutral">Inativo</Badge> : null}
                  <span className="font-mono text-xs text-text-3">
                    {t.members.length} {t.members.length === 1 ? 'pessoa' : 'pessoas'}
                  </span>
                  {can('teams.edit') ? (
                    <Menu>
                      <MenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="ml-auto"
                          aria-label="Ações"
                        >
                          <MoreHorizontal />
                        </Button>
                      </MenuTrigger>
                      <MenuContent align="end">
                        <MenuItem onSelect={() => setEditing(t)}>
                          <Pencil /> Editar
                        </MenuItem>
                        {can('teams.delete') ? (
                          <MenuItem danger onSelect={() => setRemove(t)}>
                            <Trash2 /> Excluir
                          </MenuItem>
                        ) : null}
                      </MenuContent>
                    </Menu>
                  ) : null}
                </header>
                {t.description ? <p className="mb-3 text-sm text-text-2">{t.description}</p> : null}
                <ul className="grid gap-1.5 sm:grid-cols-2">
                  {t.members.map((m) => (
                    <li key={m.user.id} className="flex items-center gap-2 text-sm">
                      <Avatar name={m.user.name} size="sm" />
                      <span className="truncate">{m.user.name}</span>
                      {m.role === 'MANAGER' ? <Badge tone="accent">Gestor</Badge> : null}
                      <span className="ml-auto flex gap-1">
                        {m.functions.map((f) => (
                          <Badge key={f.id} mono={false} tone="neutral">
                            {f.name}
                          </Badge>
                        ))}
                      </span>
                    </li>
                  ))}
                  {t.members.length === 0 ? (
                    <li className="text-sm text-text-3">Sem membros.</li>
                  ) : null}
                </ul>
              </section>
            ))
          )}
        </div>
        <FunctionsPanel />
      </div>
      <TeamDialog
        open={editing !== null}
        team={editing === 'new' ? undefined : (editing ?? undefined)}
        onOpenChange={(o) => !o && setEditing(null)}
      />
      <ConfirmDialog
        open={remove !== null}
        onOpenChange={() => setRemove(null)}
        title={`Excluir time ${remove?.name ?? ''}`}
        description="Os membros não são afetados; só o agrupamento some."
        confirmLabel="Excluir"
        danger
        loading={del.isPending}
        onConfirm={() =>
          remove &&
          del.mutate(remove.id, {
            onSuccess: () => (toast.success('Time excluído'), setRemove(null)),
            onError: (e) => toast.error(e),
          })
        }
      />
    </div>
  );
}

function FunctionsPanel() {
  const toast = useToast();
  const { can } = useAuth();
  const { data: functions = [] } = useFunctions();
  const [name, setName] = useState('');
  const create = useInvalidating(
    (n: string) => api.post<FunctionRow>('/functions', { name: n }),
    [keys.functions],
  );
  const del = useInvalidating(
    (id: string) => api.delete(`/functions/${id}`),
    [keys.functions, keys.teams],
  );
  return (
    <aside className="flex flex-col gap-3 self-start rounded-md border border-border bg-surface-1 p-4">
      <LabelCaps>Funções</LabelCaps>
      <p className="text-sm text-text-2">
        Templates de workflow atribuem responsáveis por função (ex.: Gestor de Tráfego).
      </p>
      <ul className="flex flex-col gap-1">
        {functions.map((f) => (
          <li key={f.id} className="group flex h-7 items-center gap-2 text-sm">
            <span className="flex-1">{f.name}</span>
            {can('teams.edit') ? (
              <button
                type="button"
                aria-label="Remover"
                className="text-text-3 opacity-0 hover:text-danger group-hover:opacity-100"
                onClick={() => del.mutate(f.id, { onError: (e) => toast.error(e) })}
              >
                <X className="size-3" />
              </button>
            ) : null}
          </li>
        ))}
        {functions.length === 0 ? (
          <li className="text-sm text-text-3">Nenhuma função ainda.</li>
        ) : null}
      </ul>
      {can('teams.edit') ? (
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim().length < 2) return;
            create.mutate(name.trim(), {
              onSuccess: () => setName(''),
              onError: (err) => toast.error(err),
            });
          }}
        >
          <Input
            placeholder="Nova função"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-control-sm text-sm"
          />
          <Button type="submit" variant="secondary" size="sm" loading={create.isPending}>
            <Plus />
          </Button>
        </form>
      ) : null}
    </aside>
  );
}

function TeamDialog({
  open,
  onOpenChange,
  team,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  team?: Team;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={team ? `Editar ${team.name}` : 'Novo time'} size="lg">
        <TeamForm team={team} onOpenChange={onOpenChange} />
      </DialogContent>
    </Dialog>
  );
}

function TeamForm({ team, onOpenChange }: { team?: Team; onOpenChange: (o: boolean) => void }) {
  const toast = useToast();
  const { data: functions = [] } = useFunctions();
  const { data: usersData } = useUsers({ status: 'ACTIVE' });
  const users = usersData?.data ?? [];
  const [name, setName] = useState(team?.name ?? '');
  const [description, setDescription] = useState(team?.description ?? '');
  const [members, setMembers] = useState<TeamMemberInput[]>(() =>
    team
      ? team.members.map((m) => ({
          user_id: m.user.id,
          role: m.role,
          function_ids: m.functions.map((f) => f.id),
        }))
      : [],
  );

  const save = useInvalidating(async () => {
    if (team) {
      await api.patch(`/teams/${team.id}`, { name, description: description || null });
      return api.put(`/teams/${team.id}/members`, { members });
    }
    return api.post('/teams', { name, description: description || undefined, members });
  }, [keys.teams]);

  const setMember = (uid: string, patch: Partial<TeamMemberInput>) =>
    setMembers(members.map((m) => (m.user_id === uid ? { ...m, ...patch } : m)));

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(undefined, {
          onSuccess: () => (toast.success('Time salvo'), onOpenChange(false)),
          onError: (err) => toast.error(err),
        });
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Nome" htmlFor="tm-name">
          <Input id="tm-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Descrição" htmlFor="tm-desc">
          <Input
            id="tm-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <LabelCaps>Membros</LabelCaps>
          <UsersMultiPicker
            value={members.map((m) => m.user_id)}
            onChange={(ids) =>
              setMembers([
                ...members.filter((m) => ids.includes(m.user_id)),
                ...ids
                  .filter((id) => !members.some((m) => m.user_id === id))
                  .map((id) => ({ user_id: id, role: 'MEMBER' as const, function_ids: [] })),
              ])
            }
          />
        </div>
        {members.length === 0 ? (
          <p className="text-sm text-text-3">Adicione pessoas ao time.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-border rounded border border-border">
            {members.map((m) => {
              const u = users.find((x) => x.id === m.user_id);
              return (
                <li
                  key={m.user_id}
                  className="grid items-center gap-3 px-3 py-2 sm:grid-cols-[1fr_auto_1fr_auto]"
                >
                  <span className="flex items-center gap-2 text-sm">
                    <Avatar name={u?.name ?? '?'} size="sm" />
                    {u?.name ?? m.user_id}
                  </span>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={m.role === 'MANAGER'}
                      onCheckedChange={(v) =>
                        setMember(m.user_id, { role: v ? 'MANAGER' : 'MEMBER' })
                      }
                    />{' '}
                    Gestor
                  </label>
                  <Select
                    value=""
                    onChange={(e) =>
                      e.target.value &&
                      !m.function_ids.includes(e.target.value) &&
                      setMember(m.user_id, { function_ids: [...m.function_ids, e.target.value] })
                    }
                    className="h-control-sm text-sm"
                  >
                    <option value="">+ função</option>
                    {functions
                      .filter((f) => !m.function_ids.includes(f.id))
                      .map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                  </Select>
                  <span className="flex flex-wrap gap-1">
                    {m.function_ids.map((fid) => (
                      <Badge key={fid} mono={false} tone="neutral" className="gap-1">
                        {functions.find((f) => f.id === fid)?.name}
                        <button
                          type="button"
                          aria-label="Remover função"
                          onClick={() =>
                            setMember(m.user_id, {
                              function_ids: m.function_ids.filter((x) => x !== fid),
                            })
                          }
                        >
                          <X className="size-2.5" />
                        </button>
                      </Badge>
                    ))}
                    <button
                      type="button"
                      aria-label="Remover membro"
                      className="text-text-3 hover:text-danger"
                      onClick={() => setMembers(members.filter((x) => x.user_id !== m.user_id))}
                    >
                      <X className="size-3.5" />
                    </button>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <DialogFooter>
        <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="submit"
          loading={save.isPending}
          disabled={name.trim().length < 2}
        >
          Salvar
        </Button>
      </DialogFooter>
    </form>
  );
}
